import {
	DefaultButtonCompat as DefaultButton,
	IconButtonCompat as IconButton,
	LabelCompat as Label,
	PanelCompat as Panel,
	PrimaryButtonCompat as PrimaryButton,
	SeparatorCompat as Separator,
	StackCompat as Stack,
} from 'components/FluentUICompat';
import {PanelType} from 'components/FluentUICompat';
import {useUserContext} from 'authentication/UserContext';
import {ConfirmUpdateDialog, useDialogState} from 'components/Dialogs';
import {
	ControlledDropdown,
	ControlledRadioGroup,
	ControlledTagPicker,
	ControlledTextField,
} from 'components/hookForms';
import {GetWorkbenchItemsVexDocument} from 'features/Dashboard/hooks/workbenchItems.generated';
import {useRegulatoryDocumentsContext} from 'features/RegulatoryDocuments/context';
import {useConcludeRegulatoryDocumentMutation} from 'features/RegulatoryDocuments/hooks/useConcludeRegulatoryDocument.generated';
import {
	GetRegulatoryDocumentDetailsDocument,
	GetVexRegulatoryDocumentParagraphsDocument,
} from 'features/RegulatoryDocuments/hooks/useGetRegulatoryDocumentDetails.generated';
import {useUpdateVexClusterAssignmentBatchMutation} from 'features/RegulatoryDocuments/hooks/useUpdateVexClusterAssignment.generated';
import {ProviderThatEnablesGettingTooltipsFromContext} from 'features/localizedTooltips';
import {useCommand, useNotificationBar} from 'hooks';
import React, {useMemo} from 'react';
import {useFieldArray, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {
	Keyword,
	KeywordStatus,
	RejectReasonOption,
	UserRole,
	WorkflowStatus,
} from 'types';
import {KeywordAssignmentFormTooltipTranslationProvider} from './KeywordAssignmentFormTooltipTranslationProvider';
import {useParagraphsContext} from './ParagraphsContext';
import {useKeywordAssignmentsFormData} from './hooks/useKeywordAssignmentsFormData';
import {useDoubleIconCommandButton} from '../../hooks/useDoubleIconCommandButton';
import {AppendButton} from 'components/hookForms/AppendButton';

enum FormMode {
	None,
	Reject,
	Approve,
	RequirementsDerived,
}

type KeywordAssignmentFormValues = {
	keywordId: string;
	vexClusterId: string;
	rejectReason?: string;
	keywords?: Keyword[];
	rejectReasonOption: RejectReasonOption;
	rejectKeywordReason?: string;
	suggestions?: {suggestedKeyword?: any; suggestionNote?: string}[];
};

export const KeywordAssignmentForm = () => {
	/** Hooks */
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'KeywordAssignmentForm',
	});

	const [searchParams] = useSearchParams();
	const filter = searchParams.get('filter') ?? '';

	const [formMode, setFormMode] = React.useState<FormMode>(FormMode.None);
	const [reasonOption, setReasonOption] = React.useState<RejectReasonOption>(
		RejectReasonOption.NoDerivation,
	);
	const headerText = useMemo(() => {
		if (formMode === FormMode.Reject) {
			return t('RejectKeywordPanelHeader');
		}

		return t('ApproveKeywordPanelHeader');
	}, [formMode]);

	const {selectedRegulatoryDocument} = useRegulatoryDocumentsContext();
	const {selectedParagraphs} = useParagraphsContext();
	const {isVex, isAdmin, isVexSupport, myVexClusters} = useUserContext();
	const {createDoubleIconCommandButton} = useDoubleIconCommandButton();
	const [updateDialogState, showUpdateDialog, hideUpdateDialog] =
		useDialogState();

	const [updateVexClusterAssignmentBatch] =
		useUpdateVexClusterAssignmentBatchMutation();

	const [concludeRegulatoryDocument] = useConcludeRegulatoryDocumentMutation();
	const {setMessage} = useNotificationBar();

	const {
		handleSubmit,
		control,
		reset,
		watch,
		formState: {isValid},
	} = useForm<KeywordAssignmentFormValues>({
		reValidateMode: 'onSubmit',
		mode: 'all',
	});

	const {fields, remove, append} = useFieldArray({
		control,
		name: 'suggestions',
	});

	const {
		additionalKeywordOptions,
		defaultKeyword,
		defaultVexCluster,
		selectableKeywords,
		selectableVexClusters,
	} = useKeywordAssignmentsFormData(watch('keywordId'));

	React.useEffect(() => {
		if (formMode !== FormMode.None) {
			reset({
				suggestions: [],
				keywordId: defaultKeyword?.id,
				vexClusterId: defaultVexCluster?.id,
			});
		}
	}, [formMode]);

	const selectableSuggestionKeywords = useMemo(() => {
		const allSelectedKeywords = selectedParagraphs.flatMap(para =>
			para.keywords.map(k => k.id),
		);
		return additionalKeywordOptions.filter(
			ako => allSelectedKeywords.indexOf(ako.id) === -1,
		);
	}, [additionalKeywordOptions, selectedParagraphs]);

	const keywordOptions = useMemo(() => {
		return selectableKeywords.map(k => ({
			key: k.id,
			text: k.name,
		}));
	}, [selectableKeywords]);

	const vexClusterOptions = useMemo(() => {
		return selectableVexClusters.map(vc => ({
			key: vc.id,
			text: vc.name,
		}));
	}, [selectableVexClusters]);

	useCommand(
		{
			key: 'keywordAssignment',
			text: t('KeywordAssignmentCommand'),
			priority: -1,
			iconProps: {
				iconName: 'ReminderGroup',
			},
			hidden:
				selectedRegulatoryDocument?.workflow?.status !==
				WorkflowStatus.Finalized,
			roles: [UserRole.SystemAdministrator, UserRole.Vex],
			disabled: selectedParagraphs.length === 0 || isVexSupport,
			title: t('KeywordAssignmentCommandTooltip'),
			subMenuProps: {
				items: [
					createDoubleIconCommandButton(
						{
							key: 'approveAssignment',
							text: t('ApproveCommand'),
							onClick() {
								setFormMode(FormMode.Approve);
							},
						},
						'check',
					),
					createDoubleIconCommandButton(
						{
							key: 'rejectAssignment',
							text: t('RejectCommand'),
							onClick() {
								setFormMode(FormMode.Reject);
							},
						},
						'dismiss',
					),
				],
			},
		},
		[formMode, selectedParagraphs?.length],
	);

	const paragraphsToDo = useMemo(() => {
		if (!selectedRegulatoryDocument?.paragraphs) {
			return undefined;
		}

		const myVexClusterIds = myVexClusters.map(vc => vc.id);

		return selectedRegulatoryDocument.paragraphs.filter(p =>
			p.keywordAssignments?.some(kwa =>
				kwa.vexClusterAssignments.some(
					vca =>
						myVexClusterIds.includes(vca.vexCluster.id) &&
						vca.keywordStatus === KeywordStatus.New,
				),
			),
		);
	}, [selectedRegulatoryDocument, myVexClusters]);

	const isConcludeDisabled = useMemo(() => {
		return paragraphsToDo?.length !== 0;
	}, [paragraphsToDo]);

	useCommand(
		{
			farCommand: true,
			priority: -1,
			title: isConcludeDisabled
				? t('ConcludeCommandDisabled', {count: paragraphsToDo?.length})
				: t('ConcludeCommand'),
			key: 'concludeDocument',
			iconProps: {
				iconName: 'CheckboxComposite',
			},
			roles: [UserRole.SystemAdministrator, UserRole.Vex],
			disabled: isConcludeDisabled,
			hidden: filter !== 'inProcess',
			onClick() {
				showUpdateDialog();
			},
		},
		[
			isVex,
			isAdmin,
			selectedRegulatoryDocument,
			isConcludeDisabled,
			paragraphsToDo,
			filter,
		],
	);

	/** Submit handlers */
	const keywordStatus = useMemo(() => {
		if (formMode === FormMode.Reject) {
			return reasonOption === RejectReasonOption.NoDerivation
				? KeywordStatus.NoImpact
				: KeywordStatus.Declined;
		}

		if (formMode === FormMode.Approve) {
			return KeywordStatus.Accepted;
		}

		if (formMode === FormMode.RequirementsDerived) {
			return KeywordStatus.RequirementsDerived;
		}

		return KeywordStatus.New;
	}, [formMode, reasonOption]);

	const handleSubmitClick = React.useCallback(() => {
		handleSubmit((item: KeywordAssignmentFormValues) => {
			const suggestions =
				item.suggestions?.map(s => {
					return {
						suggestedKeywordId: s?.suggestedKeyword?.[0]?.id || '',
						suggestionNote: s.suggestionNote || '',
					};
				}) || [];
			updateVexClusterAssignmentBatch({
				variables: {
					input: {
						keywordId: item.keywordId,
						keywordStatus,
						paragraphIds: selectedParagraphs?.map(p => p.id),
						regulatoryDocumentId: selectedRegulatoryDocument?.id ?? '',
						rejectReason:
							formMode === FormMode.Reject ? item.rejectReason ?? '' : '',
						vexClusterId: item.vexClusterId,
						rejectReasonOption: reasonOption,
						rejectKeywordReason: item.rejectKeywordReason ?? '',
						suggestions,
					},
				},
				refetchQueries: [
					GetWorkbenchItemsVexDocument,
					GetVexRegulatoryDocumentParagraphsDocument,
					GetRegulatoryDocumentDetailsDocument,
				],
			});

			if (item) {
				if (formMode === FormMode.Reject) {
					setMessage(t('RejectMessage'));
				}

				if (formMode === FormMode.Approve) {
					setMessage(t('ApproveMessage'));
				}

				if (formMode === FormMode.RequirementsDerived) {
					setMessage(t('RequirementsDerivedMessage'));
				}
			}

			setFormMode(FormMode.None);
			reset({});
		})();
	}, [selectedParagraphs, selectedRegulatoryDocument, formMode, keywordStatus]);

	const handleCancelClick = React.useCallback(() => {
		setReasonOption(RejectReasonOption.NoDerivation);
		setFormMode(FormMode.None);
		reset({});
	}, [formMode]);

	const handleConclusionSubmit = React.useCallback(() => {
		handleSubmit(async () => {
			await concludeRegulatoryDocument({
				variables: {
					input: {
						regulatoryDocumentId: selectedRegulatoryDocument?.id ?? '',
					},
				},
				refetchQueries: [
					GetVexRegulatoryDocumentParagraphsDocument,
					GetRegulatoryDocumentDetailsDocument,
				],
			});
		})().catch(error => console.error('Error:', error));
	}, [selectedRegulatoryDocument]);

	const onConfirmConclusion = React.useCallback(() => {
		handleConclusionSubmit();
		hideUpdateDialog();
	}, [handleConclusionSubmit]);

	/** Component */
	const renderFooterContent: any = () => (
		<form>
			{formMode === FormMode.Reject && (
				<PrimaryButton
					styles={PrimaryButtonStyles}
					onClick={handleSubmitClick}
					disabled={!isValid}
				>
					{t('RejectButton')}
				</PrimaryButton>
			)}
			{formMode === FormMode.Approve && (
				<PrimaryButton
					styles={PrimaryButtonStyles}
					onClick={handleSubmitClick}
					disabled={!isValid}
				>
					{t('ApproveButton')}
				</PrimaryButton>
			)}
			{formMode === FormMode.RequirementsDerived && (
				<PrimaryButton
					styles={PrimaryButtonStyles}
					onClick={handleSubmitClick}
					disabled={!isValid}
				>
					{t('ConfirmButton')}
				</PrimaryButton>
			)}
			<DefaultButton onClick={handleCancelClick}>
				{t('CancelButton')}
			</DefaultButton>
		</form>
	);

	const getKeywordSuggestion = (item: any, index: any) => {
		return (
			<Stack key={item.id}>
				<Stack.Item>
					<Stack horizontal>
						<Stack.Item styles={{root: {width: '95%'}}}>
							<ControlledTagPicker
								label={t('Keyword')}
								name={`suggestions[${index}].suggestedKeyword`}
								control={control}
								selectableItems={selectableSuggestionKeywords}
								itemLimit={1}
								rules={{required: true}}
							/>
						</Stack.Item>
						<Stack.Item>
							<IconButton
								styles={{
									root: {marginTop: 28},
								}}
								iconProps={{iconName: 'Delete'}}
								onClick={() => remove(index)}
							/>
						</Stack.Item>
					</Stack>
				</Stack.Item>
				<Stack.Item>
					<ControlledTextField
						label={t('SuggestionNote')}
						name={`suggestions[${index}].suggestionNote`}
						control={control}
						multiline={false}
						autoAdjustHeight
						required={true}
						rules={{required: true}}
					/>
				</Stack.Item>
				<Stack.Item>
					<Separator />
				</Stack.Item>
			</Stack>
		);
	};

	return (
		<React.Fragment>
			<Panel
				isLightDismiss
				isFooterAtBottom={true}
				onDismiss={handleCancelClick}
				isOpen={formMode !== FormMode.None}
				type={PanelType.medium}
				closeButtonAriaLabel='Close'
				headerText={headerText}
				onRenderFooterContent={renderFooterContent}
			>
				<KeywordAssignmentFormTooltipTranslationProvider
					treatAsDottedFormFieldName={true}
				>
					<ProviderThatEnablesGettingTooltipsFromContext>
						<ControlledDropdown
							control={control}
							name='keywordId'
							label={t('KeywordDropdownLabel')}
							options={keywordOptions}
							defaultValue={defaultKeyword?.id}
							rules={{required: t('KeywordDropdownRequired')}}
							required
						/>
						<ControlledDropdown
							control={control}
							name='vexClusterId'
							label={t('VexClusterDropdownLabel')}
							options={vexClusterOptions}
							defaultValue={defaultVexCluster?.id}
							rules={{required: t('VexClusterDropdownRequired')}}
							required
						/>
						{formMode === FormMode.Reject && (
							<ControlledRadioGroup
								control={control}
								name='reasonOption'
								defaultValue={reasonOption}
								values={[
									RejectReasonOption.NoDerivation,
									RejectReasonOption.WrongKeyword,
								]}
								translations={[
									t('ReasonOptionNoDerivation'),
									t('ReasonOptionWrongKeyword'),
								]}
								onValueChanged={(newVal: any) => {
									setReasonOption(newVal as RejectReasonOption);
								}}
								required
							/>
						)}
						{formMode === FormMode.Reject &&
							reasonOption === RejectReasonOption.WrongKeyword && (
								<ControlledTextField
									label={t('RejectKeywordReasonTextFieldLabel')}
									name={'rejectKeywordReason'}
									control={control}
									multiline={false}
									autoAdjustHeight
									required
								/>
							)}

						<React.Fragment>
							{formMode === FormMode.Reject && (
								<ControlledTextField
									label={t('RejectReasonTextFieldLabel')}
									name={'rejectReason'}
									control={control}
									multiline
									autoAdjustHeight
								/>
							)}
							{(formMode === FormMode.Approve ||
								formMode === FormMode.Reject) && (
								<>
									<Label>
										<b>{t('KeywordsLabel')}</b>
									</Label>
									{fields.map((item: any, index: any) =>
										getKeywordSuggestion(item, index),
									)}
									<Stack.Item>
										<AppendButton
											onClick={() => {
												append({});
											}}
											label={t('Keyword')}
											name={`regulatories`}
										/>
									</Stack.Item>
								</>
							)}
						</React.Fragment>
					</ProviderThatEnablesGettingTooltipsFromContext>
				</KeywordAssignmentFormTooltipTranslationProvider>
			</Panel>
			<ConfirmUpdateDialog
				{...updateDialogState}
				onConfirm={onConfirmConclusion}
				dialogContentProps={{
					subText: t('ConfirmDialog'),
					title: t('ConcludeCommand'),
				}}
				confirmButtonLabel={t('ConfirmButtonLabel')}
			/>
		</React.Fragment>
	);
};

const PrimaryButtonStyles = {
	root: {
		marginRight: 8,
	},
};
